import { makeStyles, tokens } from '@fluentui/react-components';
import { NAVY } from '#src/theme/identity';

export const EXPANDED_WIDTH = '272px';
export const COLLAPSED_WIDTH = '68px';
/**
 * Altura do cabeçalho da navegação e da barra do topo: as duas linhas de
 * divisão ficam na mesma altura, de ponta a ponta da tela.
 */
export const SHELL_HEADER_HEIGHT = '56px';
const HANDLE_SIZE = '24px';

const focusRing = {
  outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}`,
  outlineOffset: '-2px',
};

const reducedMotion = {
  '@media (prefers-reduced-motion: reduce)': { transitionDuration: '0.01ms' },
};

const thinScrollbar = {
  scrollbarWidth: 'thin' as const,
  scrollbarColor: `${tokens.colorNeutralStroke1} transparent`,
};

export const useSidebarStyles = makeStyles({
  // Envolve a barra para a alça poder ficar sobre a borda, fora do recorte.
  wrapper: {
    position: 'relative',
    display: 'flex',
    flexShrink: 0,
    height: '100%',
  },
  // Alça de recolher/expandir: sobre a borda direita, no meio do cabeçalho.
  handle: {
    position: 'absolute',
    top: `calc((${SHELL_HEADER_HEIGHT} - ${HANDLE_SIZE}) / 2)`,
    right: '-12px',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: HANDLE_SIZE,
    height: HANDLE_SIZE,
    padding: 0,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    color: tokens.colorNeutralForeground2,
    fontSize: '14px',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorBrandForeground1,
      border: `1px solid ${tokens.colorBrandStroke1}`,
    },
    ':focus-visible': { ...focusRing, outlineOffset: '2px' },
  },
  arrow: {
    transitionProperty: 'transform',
    transitionDuration: tokens.durationNormal,
    ...reducedMotion,
  },
  arrowCollapsed: { transform: 'rotate(180deg)' },
  close: { flexShrink: 0, color: tokens.colorNeutralForeground3 },
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    width: EXPANDED_WIDTH,
    height: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
    // O fundo do Nav do Fluent 2 (NavDrawer). A borda transparente só
    // aparece no alto contraste, separando a barra da página.
    backgroundColor: tokens.colorNeutralBackground4,
    borderRight: `1px solid ${tokens.colorTransparentStroke}`,
    transitionProperty: 'width',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    ...reducedMotion,
  },
  // Com a identidade: o azul desce para um tom mais fundo.
  rootIdentity: {
    backgroundImage: `linear-gradient(180deg, ${NAVY.base} 0%, ${NAVY.deep} 100%)`,
  },
  rootCollapsed: { width: COLLAPSED_WIDTH },
  // A assinatura da etiqueta no pé da barra.
  stars: {
    flexShrink: 0,
    alignSelf: 'center',
    margin: `0 0 ${tokens.spacingVerticalL}`,
    color: tokens.colorCompoundBrandForeground1,
  },
  // Na gaveta, a barra ocupa toda a largura do painel.
  rootDrawer: { width: '100%', borderRight: 'none' },
  drawerBody: { padding: 0 },

  // Cabeçalho: mesma altura e posição da marca nos dois estados.
  header: {
    display: 'flex',
    alignItems: 'center',
    height: SHELL_HEADER_HEIGHT,
    flexShrink: 0,
    padding: '0 10px',
    boxSizing: 'border-box',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexGrow: 1,
    minWidth: 0,
    padding: tokens.spacingHorizontalXS,
    border: 'none',
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: 'transparent',
    color: 'inherit',
    font: 'inherit',
    textAlign: 'start',
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground4Hover },
    ':focus-visible': focusRing,
  },
  // O ícone do app já é um bloco com fundo e cantos (o mesmo do Teams): vai
  // inteiro, sem outro fundo em volta.
  brandLogo: {
    display: 'block',
    width: '40px',
    height: '40px',
    flexShrink: 0,
    borderRadius: tokens.borderRadiusXLarge,
  },
  brandText: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  // O nome quebra em duas linhas em vez de ser cortado com reticências.
  brandName: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    display: '-webkit-box',
    WebkitLineClamp: '2',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  subtle: { color: tokens.colorNeutralForeground3 },

  // Área dos módulos. Quando falta altura, encolhem e rolam as listas de
  // páginas das pastas, não a barra inteira; esta só rola em último caso.
  scroll: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM} ${tokens.spacingVerticalL}`,
    ...thinScrollbar,
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 1,
    minHeight: 0,
  },
  // Rótulo de seção. Altura fixa: recolhida, a barra mostra um traço no mesmo
  // espaço, para os ícones abaixo não mudarem de posição.
  section: {
    display: 'flex',
    alignItems: 'center',
    height: '28px',
    flexShrink: 0,
    padding: `0 ${tokens.spacingHorizontalM}`,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    whiteSpace: 'nowrap',
  },
  sectionDash: {
    width: '20px',
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
  },
  item: {
    position: 'relative',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    width: '100%',
    minHeight: '40px',
    padding: `0 ${tokens.spacingHorizontalM}`,
    boxSizing: 'border-box',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: tokens.borderRadiusMedium,
    color: tokens.colorNeutralForeground2,
    fontFamily: 'inherit',
    fontSize: tokens.fontSizeBase300,
    textAlign: 'start',
    cursor: 'pointer',
    transitionProperty: 'background',
    transitionDuration: tokens.durationFaster,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground4Hover,
      color: tokens.colorNeutralForeground1,
    },
    ':active': { backgroundColor: tokens.colorNeutralBackground4Pressed },
    ':focus-visible': focusRing,
    ...reducedMotion,
  },
  // Recolhida: só o ícone, na mesma posição e altura do item expandido.
  itemCollapsed: { width: '44px' },
  groupHeader: { color: tokens.colorNeutralForeground1 },
  // O módulo da página aberta: em negrito, como a categoria do Nav.
  groupActive: { fontWeight: tokens.fontWeightSemibold },
  subItem: {
    minHeight: '36px',
    // Alinha o texto com o título do módulo (ícone de 20px + espaçamento).
    paddingLeft: `calc(${tokens.spacingHorizontalM} + 20px + ${tokens.spacingHorizontalM})`,
  },
  // Selecionado como no Nav do Fluent 2: texto em negrito e o traço da cor
  // da marca à esquerda, sem pintar o fundo.
  selected: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    '::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      width: '4px',
      height: '20px',
      marginTop: '-10px',
      borderRadius: tokens.borderRadiusCircular,
      backgroundColor: tokens.colorCompoundBrandForeground1,
      '@media (forced-colors: active)': { backgroundColor: 'Highlight' },
    },
  },
  icon: { display: 'flex', flexShrink: 0, fontSize: '20px' },
  iconSelected: { color: tokens.colorNeutralForeground2BrandSelected },
  label: {
    flexGrow: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chevron: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
    transitionProperty: 'transform',
    transitionDuration: tokens.durationFast,
    ...reducedMotion,
  },
  chevronClosed: { transform: 'rotate(-90deg)' },
  subList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    listStyleType: 'none',
    margin: `${tokens.spacingVerticalXXS} 0 ${tokens.spacingVerticalXS}`,
    padding: 0,
    flexShrink: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    ...thinScrollbar,
  },
  subListItem: { flexShrink: 0 },
});
